import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listQuotations, deleteQuotation } from './quotationApi.js';

const statusColor = (status) => (status === 'draft' ? 'warning' : 'success');

const QuotationsListPage = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const data = await listQuotations();
      setQuotations(data);
      setError(null);
    } catch (err) {
      setError('Could not load your quotations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const handleDelete = async (id, event) => {
    event.stopPropagation();
    if (!window.confirm('Delete this quotation? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteQuotation(id);
      setQuotations((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      setError('Could not delete this quotation.');
    } finally {
      setDeletingId(null);
    }
  };

  const draft = quotations.find((q) => q.status === 'draft');
  const history = quotations.filter((q) => q.status !== 'draft');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight="bold">Quotations</Typography>
        <Button variant="outlined" onClick={() => navigate('/products/list')}>
          Browse Products
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {draft && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{ p: 2, mb: 4, borderRadius: 2, borderColor: 'primary.main', borderWidth: 2 }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon fontSize="small" color="primary" />
                    Current Quotation (Draft)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {draft.item_count} item(s) - ${draft.total}
                  </Typography>
                </Box>
                <Button variant="contained" onClick={() => navigate(`/quotations/${draft.id}`)}>
                  Open Basket
                </Button>
              </Stack>
            </Paper>
          )}

          <Typography variant="h6" sx={{ mb: 1 }}>History</Typography>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No saved quotations yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((q) => (
                    <TableRow
                      key={q.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/quotations/${q.id}`)}
                    >
                      <TableCell>{q.name || `Quotation #${q.id}`}</TableCell>
                      <TableCell>
                        <Chip size="small" label={q.status} color={statusColor(q.status)} />
                      </TableCell>
                      <TableCell align="right">{q.item_count}</TableCell>
                      <TableCell align="right">${q.total}</TableCell>
                      <TableCell>{new Date(q.updated_at).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${q.id}`); }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleDelete(q.id, e)}
                              disabled={deletingId === q.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Container>
  );
};

export default QuotationsListPage;
